import React, { createContext, useEffect, useState } from 'react';
import client from '../util/storefrontApiClient';

const isBrowser = typeof window !== `undefined`

const CART_QUERY = `
  cart {
    id
    checkoutUrl
    lines(first: 99) {
      nodes {
        id
        merchandise {
          ... on ProductVariant {
            id
            title
            product {
              id
              handle
              title
            }
            image {
              url
              altText
            }
          }
        }
        discountAllocations {
          discountedAmount {
            amount
          }
        }
        cost {
          amountPerQuantity {
            amount
          }
          compareAtAmountPerQuantity {
            amount
          }
        }
        quantity
      }
    }
    discountAllocations {
      discountedAmount {
        amount
      }
    }
    cost {
      subtotalAmount {
        amount
      }
      totalAmount {
        amount
      }
      totalTaxAmount {
        amount
      }
    }
    discountCodes {
      code
      applicable
    }
  }
  userErrors {
    field
    message
  }
  warnings {
    code
    message
    target
  }
`

const LOGIN_MUTATION = `
  mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      customerAccessToken {
        accessToken
        expiresAt
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }
`;

const USER_QUERY = `query($customerAccessToken: String!) {
  customer(customerAccessToken: $customerAccessToken) {
    id
    firstName
    lastName
    email
  }
}`;

const getLocalStorageJson = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(localStorage.getItem(key)) || null;
  } catch {
    return null;
  }
};

const getLocalStorage = (key) => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(key) || null;
};

const defaultValues = {
  cart: {},
  cartIsOpen: false,
  setCartIsOpen: () => {},
  scrollDisabled: false,
  setScrollDisabled: () => {},
  addVariantToCart: () => {},
  addVariantsToCart: () => {},
  updateLineItem: () => {},
  removeLineItems: () => {},
  addCartDiscount: () => {},
  removeCartDiscount: () => {},
  cartNotification: {},
  setCartNotification: () => {},
  globalNotification: {},
  setGlobalNotification: () => {},
  addPreviouslyViewedProduct: () => {},
  previouslyViewedProducts: () => [],
  customerAccessToken: '',
  currentUser: {},
  handleLogin: () => {},
  handleLogout: () => {},
  setCurrentUser: () => {},
  wishlist: [],
  toggleWishlist: () => {},
  searchTerm: '',
  setSearchTerm: () => {},
}
export const ThemeContext = createContext(defaultValues)

export const ThemeProvider = ({ children }) => {
  const [cart, setCart] = useState({});
  const [cartIsOpen, setCartIsOpen] = useState(false);
  const [scrollDisabled, setScrollDisabled] = useState(false);
  const [cartNotification, setCartNotification] = useState({});
  const [globalNotification, setGlobalNotification] = useState({});
  const [currentUser, setCurrentUser] = useState(getLocalStorageJson('currentUser'));
  const [customerAccessToken, setCustomerAccessToken] = useState(getLocalStorage('customerAccessToken'));
  const [wishlist, setWishlist] = useState(getLocalStorageJson('wishlist') || []);
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * Prevent body scroll if scrollDisabled
   */
  useEffect(() => {
    if (!isBrowser) return;
    document.body.style.overflow = scrollDisabled ? 'hidden' : null;
  }, [scrollDisabled])

  /**
   * Get saved cart from storage on page reload
   */
  useEffect(() => {
    if(!cart.id) {
      const savedCart = getCartState();
      if(savedCart) {
        setCart(savedCart);
      }
    }
  }, [cart.id])

  const saveCartState = (cart) => {
    setCart(cart);
    localStorage.setItem('shopify_cart', JSON.stringify(cart));
  }

  const getCartState = () => {
    let cartData = localStorage.getItem('shopify_cart');
    if(cartData) {
      setCart(JSON.parse(cartData))
      return JSON.parse(cartData);
    }
    return false;
  }

  /**
   * Set customerAccessToken and currentUser states
   */
  useEffect(() => {
    if (customerAccessToken && !currentUser) {
      getUserData(customerAccessToken);
    } else if(!customerAccessToken && currentUser) {
      setCurrentUser(null);
      localStorage.removeItem('currentUser');
    }
  }, [customerAccessToken, currentUser]);

  /**
   * Create new Shopify cart object
   * @param {array} lines
   * @returns
   */
  const createCart = async (lines = []) => {
    const createCartMutation = `mutation createCart($cartInput: CartInput) {
      cartCreate(input: $cartInput) {
        ${CART_QUERY}
      }
    }`;

    const cartInput = {
      lines: lines
    }

    if(currentUser && currentUser.email) {
      cartInput.buyerIdentity = {
        email: currentUser.email,
      }
    }

    const { data, errors } = await client.request(createCartMutation, {
      variables: {
        cartInput: cartInput,
      },
    });

    if(data) {
      saveCartState(data.cartCreate.cart)
    }

    if(errors) {
      console.error(errors);
      setGlobalNotification({
        message: 'Something went wrong, cart not created or items could not be added.',
        type: 'error',
      })
      return false;
    }

    setCartNotification({
      message: 'Item added to cart!',
      type: 'success',
    })

    return data;
  }

  /**
   * Add lines to Shopify cart
   * @param {array} lines
   * @returns
   */
  const addCartLines = async (lines) => {
    const cartLinesAddMutation = `mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        ${CART_QUERY}
      }
    }`;

    const { data, errors } = await client.request(cartLinesAddMutation, {
      variables: {
        cartId: cart.id,
        lines: lines,
      },
    });

    saveCartState(data.cartLinesAdd.cart)

    if(errors) {
      console.error(errors);
      setGlobalNotification({
        message: 'Something went wrong, items could not be added.',
        type: 'error',
      })
      return false;
    }

    setCartNotification({
      message: 'Item added to cart!',
      type: 'success',
    })

    return data;
  }

  /**
   * Add variant to Shopify cart
   * @param {string} variantId
   * @param {number} quantity
   * @param {boolean} openCart
   */
  const addVariantToCart = (variantId, quantity = 1, openCart = true) => {
    if(!quantity || quantity < 1) {
      setGlobalNotification({
        message: 'You must choose a quantity higher than 0!',
        type: 'error'
      })
      return false;
    }

    const lines = [
      {
        merchandiseId: variantId,
        quantity: parseInt(quantity, 10),
      },
    ]

    if(!cart.id) {
      return createCart(lines).then(() => {
        openCart && setCartIsOpen(true);
      })
    } else {
      return addCartLines(lines).then(() => {
        openCart && setCartIsOpen(true);
      })
    }
  }

  /**
   * Add multiple variants to Shopify cart, 1 quantity each
   * @param {array} variantIds
   * @param {boolean} openCart
   */
  const addVariantsToCart = (variantIds, openCart = true) => {
    const lines = variantIds.map(id => {
      return {
        merchandiseId: id,
        quantity: 1,
      }
    })

    if(!cart.id) {
      createCart(lines).then(() => {
        openCart && setCartIsOpen(true);
      })
    } else {
      addCartLines(lines).then(() => {
        openCart && setCartIsOpen(true);
      })
    }
  }

  /**
   * Update line in Shopify cart
   * @param {string} lineID
   * @param {number} quantity
   * @returns {array}
   */
  const updateLineItem = async (lineID, quantity) => {
    if(!cart.id) return false;

    const lines = [
      {
        id: lineID,
        quantity: parseInt(quantity) || 1,
      }
    ];

    const cartLinesAddMutation = `mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        ${CART_QUERY}
      }
    }`;

    const { data, errors } = await client.request(cartLinesAddMutation, {
      variables: {
        cartId: cart.id,
        lines: lines,
      },
    })

    saveCartState(data.cartLinesUpdate.cart)

    if(errors) {
      console.error(errors);
      setCartNotification({
        message: 'Something went wrong, line item could not be updated.',
        type: 'error',
      })
      return false;
    }

    return data;
  }

  /**
   * Remove line items from Shopify cart
   * @param {array} lineIDs
   * @returns {array}
   */
  const removeLineItems = async (lineIDs) => {
    if(!cart.id) return false;

    const cartLinesRemoveMutation = `mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        ${CART_QUERY}
      }
    }`;

    const { data, errors } = await client.request(cartLinesRemoveMutation, {
      variables: {
        cartId: cart.id,
        lineIds: lineIDs,
      },
    })

    saveCartState(data.cartLinesRemove.cart)

    if(errors) {
      console.error(errors);
      setCartNotification({
        message: 'Something went wrong, line item could not be removed.',
        type: 'error',
      })
      return false;
    }

    return data;
  }

  /**
   * Add discount to Shopify cart
   * @param {string} code
   * @returns {array}
   */
  const addCartDiscount = async (code) => {
    if(!cart.id) return false;

    const cartDiscountCodesUpdateMutation = `mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
        ${CART_QUERY}
      }
    }`;

    const { data, errors } = await client.request(cartDiscountCodesUpdateMutation, {
      variables: {
        cartId: cart.id,
        discountCodes: [code],
      },
    })

    saveCartState(data.cartDiscountCodesUpdate.cart)

    if(errors) {
      console.error(errors)
      setCartNotification({
        message: 'Something went wrong, discount could not be added.',
        type: 'error',
      })
      return false;
    }

    let message = '';
    let type = 'default';

    if (data.cartDiscountCodesUpdate?.cart?.userErrors?.length) {
      message = data.cartDiscountCodesUpdate.cart.userErrors[0].message;
    } else if(data.cartDiscountCodesUpdate?.cart?.discountCodes?.find(c => c.code === code)?.applicable === false) {
      message = 'Invalid discount code.';
      type = 'error';
    } else {
      message = 'Discount Applied!';
      type = 'success';
    }

    setCartNotification({
      message: message,
      type: type,
    })

    return data;
  }

  /**
   * Remove discount from Shopify cart
   * @param {string} code
   * @returns {array}
   */
  const removeCartDiscount = async (code) => {
    if(!cart.id) return false;

    const cartDiscountCodesUpdateMutation = `mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
        ${CART_QUERY}
      }
    }`;

    const newCodes = cart.discountCodes?.filter(({code: c}) => c !== code).map(({code: c}) => c) || [];

    const { data, errors } = await client.request(cartDiscountCodesUpdateMutation, {
      variables: {
        cartId: cart.id,
        discountCodes: newCodes,
      },
    })

    saveCartState(data.cartDiscountCodesUpdate.cart)

    if(errors) {
      console.error(errors);
      setCartNotification({
        message: 'Something went wrong, discount could not be removed.',
        type: 'error',
      })
      return false;
    }

    setCartNotification({
      message: `Discount code "${code}" removed!`,
      type: 'success',
    })

    return data;
  }

  const stripUnnecessaryProductFields = (product) => {
    return {
      storefrontId: product.storefrontId,
      createdAt: product.createdAt,
      priceRangeV2: product.priceRangeV2,
      vendor: product.vendor,
      title: product.title,
      handle: product.handle,
      featuredImage: product.featuredImage.gatsbyImageData ? product.featuredImage : (product.mainMedia.length > 0 ? product.mainMedia[0].preview?.image : null),
      hideAddToCart: product.hideAddToCart,
      hideAddToCartText: product.hideAddToCartText,
      variants: product.variants?.map(variant => ({
        price: variant.price,
        compareAtPrice: variant.compareAtPrice,
        storefrontId: variant.storefrontId,
      })),
    }
  }

  /**
   * Get previously viewed products from localStorage
   * @returns {array}
   */
  const previouslyViewedProducts = () => {
    if(!isBrowser) return [];
    let previouslyViewed = localStorage.getItem('previously_viewed_products');
    previouslyViewed = previouslyViewed ? JSON.parse(previouslyViewed) : [];
    return previouslyViewed.slice(0, 10);
  }

  /**
   * Save product to previously viewed products in localStorage
   * @param {object} product
   */
  const addPreviouslyViewedProduct = (product) => {
    if(!isBrowser) return;

    // Only keep the necessary fields
    const newProduct = stripUnnecessaryProductFields(product);

    let previouslyViewed = previouslyViewedProducts();
    const exists = previouslyViewed.find(p => p.storefrontId === newProduct.storefrontId);

    if(!exists) {
      previouslyViewed.unshift(newProduct);
      if(previouslyViewed.length > 10) {
        previouslyViewed.pop();
      }
      localStorage.setItem('previously_viewed_products', JSON.stringify(previouslyViewed));
    } else { // Move to front
      previouslyViewed = previouslyViewed.filter(p => p.storefrontId !== newProduct.storefrontId);
      previouslyViewed.unshift(newProduct);
      localStorage.setItem('previously_viewed_products', JSON.stringify(previouslyViewed));
    }
  }

  /**
   * Login user with email and password
   * @param {object} variables
   * @returns {object}
   */
  const handleLogin = async (variables) => {
    const { data, errors } = await client.request(LOGIN_MUTATION, {
      variables: {
        input: variables,
      },
    });

    if (errors) {
      console.error(errors);
      return { error: errors };
    }

    const { customerAccessToken, customerUserErrors } = data.customerAccessTokenCreate;

    if (customerUserErrors.length) {
      return { error: customerUserErrors };
    }

    localStorage.setItem('customerAccessToken', customerAccessToken.accessToken);
    setCustomerAccessToken(customerAccessToken.accessToken);

    return { data: data };
  }

  /**
   * Get user data from Shopify
   * @param {string} token
   */
  const getUserData = async (token) => {
    const { data, errors } = await client.request(USER_QUERY, {
      variables: {
        customerAccessToken: token,
      },
    });

    if (errors) {
      console.error(errors);
      return;
    }

    if (data && data.customer) {
      localStorage.setItem('currentUser', JSON.stringify(data.customer));
      setCurrentUser(data.customer);
    } else {
      localStorage.removeItem('customerAccessToken');
      localStorage.removeItem('currentUser');
      setCurrentUser(null);
      setCustomerAccessToken(null);
    }
  }

  /**
   * Logout user
   */
  const handleLogout = () => {
    localStorage.removeItem('customerAccessToken');
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    setCustomerAccessToken(null);
  }

  /**
   * Add or remove product from wishlist (depending on if it's already there)
   * @param {string} productStorefrontId
   */
  const toggleWishlist = (productStorefrontId, silent = false) => {
    if(!isBrowser) return;

    const exists = wishlist.includes(productStorefrontId);
    let newWishlist = [...wishlist];

    if(exists) {
      newWishlist = newWishlist.filter(id => id !== productStorefrontId);
      if(!silent) {
        setGlobalNotification({
          message: 'Item removed from wishlist',
          type: 'default',
        })
      }
    } else {
      newWishlist.push(productStorefrontId);
      if(!silent) {
        setGlobalNotification({
          message: 'Item added to wishlist',
          type: 'success',
        })
      }
    }

    setWishlist(newWishlist);
    localStorage.setItem('wishlist', JSON.stringify(newWishlist));
  }

  return (
    <ThemeContext.Provider value={{
      cart,
      cartIsOpen,
      setCartIsOpen,
      scrollDisabled,
      setScrollDisabled,
      addVariantToCart,
      addVariantsToCart,
      updateLineItem,
      removeLineItems,
      addCartDiscount,
      removeCartDiscount,
      cartNotification,
      setCartNotification,
      globalNotification,
      setGlobalNotification,
      addPreviouslyViewedProduct,
      previouslyViewedProducts,
      customerAccessToken,
      currentUser,
      handleLogin,
      handleLogout,
      setCurrentUser,
      wishlist,
      toggleWishlist,
      searchTerm,
      setSearchTerm,
    }}>
      {children}
    </ThemeContext.Provider>
  )
}
